import { DEFAULT_HOSTNAMES, DEFAULT_RESOLVERS } from "./resolvers";
import type { Resolver } from "./types";
import { normalizeHostnames, validateResolver } from "./validation";

const RESOLVERS_KEY = "dns-benchmark.resolvers";
const RESOLVERS_VERSION_KEY = "dns-benchmark.resolvers.version";
const RESOLVERS_VERSION = "4";
const HOSTNAMES_KEY = "dns-benchmark.hostnames";
const KNOWN_BROWSER_BLOCKED_DEFAULT_IDS = new Set([
  "quad9",
  "opendns",
  "adguard",
  "cleanbrowsing",
  "nextdns",
  "mullvad"
]);
const REMOVED_DEFAULT_RESOLVER_IDS = new Set(["dns0"]);

export function loadResolvers(): Resolver[] {
  const raw = localStorage.getItem(RESOLVERS_KEY);
  if (!raw) return structuredClone(DEFAULT_RESOLVERS);
  try {
    const parsed = JSON.parse(raw) as Resolver[];
    const valid = parsed.map(validateResolver).filter((item) => item.ok);
    if (!valid.length) return structuredClone(DEFAULT_RESOLVERS);

    const shouldApplyDefaultEnabledState =
      localStorage.getItem(RESOLVERS_VERSION_KEY) !== RESOLVERS_VERSION;
    const resolvers = mergeDefaultResolvers(
      valid.map((item) => item.value),
      shouldApplyDefaultEnabledState
    );
    if (shouldApplyDefaultEnabledState) saveResolvers(resolvers);
    return resolvers;
  } catch {
    return structuredClone(DEFAULT_RESOLVERS);
  }
}

export function saveResolvers(resolvers: Resolver[]): void {
  localStorage.setItem(RESOLVERS_KEY, JSON.stringify(resolvers));
  localStorage.setItem(RESOLVERS_VERSION_KEY, RESOLVERS_VERSION);
}

export function resetResolvers(): Resolver[] {
  const resolvers = structuredClone(DEFAULT_RESOLVERS);
  saveResolvers(resolvers);
  return resolvers;
}

function mergeDefaultResolvers(
  storedResolvers: Resolver[],
  applyDefaultEnabledState: boolean
): Resolver[] {
  const storedById = new Map(storedResolvers.map((resolver) => [resolver.id, resolver]));
  const defaultIds = new Set(DEFAULT_RESOLVERS.map((resolver) => resolver.id));
  const mergedDefaults = DEFAULT_RESOLVERS.map((resolver) => ({
    ...resolver,
    ...storedById.get(resolver.id),
    enabled: applyDefaultEnabledState
      ? defaultEnabledForMigration(resolver)
      : (storedById.get(resolver.id)?.enabled ?? resolver.enabled),
    custom: false
  }));
  const customResolvers = storedResolvers.filter(
    (resolver) =>
      !REMOVED_DEFAULT_RESOLVER_IDS.has(resolver.id) &&
      (resolver.custom || !defaultIds.has(resolver.id))
  );
  return [...mergedDefaults, ...customResolvers];
}

function defaultEnabledForMigration(resolver: Resolver): boolean {
  return KNOWN_BROWSER_BLOCKED_DEFAULT_IDS.has(resolver.id) ? false : resolver.enabled;
}

export function loadHostnames(): string[] {
  const raw = localStorage.getItem(HOSTNAMES_KEY);
  if (!raw) return [...DEFAULT_HOSTNAMES];
  try {
    const parsed = JSON.parse(raw) as string[];
    const normalized = normalizeHostnames(parsed);
    return normalized.ok ? normalized.value : [...DEFAULT_HOSTNAMES];
  } catch {
    return [...DEFAULT_HOSTNAMES];
  }
}

export function saveHostnames(hostnames: string[]): void {
  localStorage.setItem(HOSTNAMES_KEY, JSON.stringify(hostnames));
}

export function resetHostnames(): string[] {
  const hostnames = [...DEFAULT_HOSTNAMES];
  saveHostnames(hostnames);
  return hostnames;
}
